import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInteractionStore } from '../../stores/useInteractionStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { getMediaUrl } from '../../utils/imageUtils';
import { getProfileLink } from '../../utils/getProfileLink';

const avatarFallback = '/images/avatar/DefaultProfile.jpg';

const FriendsWidget = () => {
  const navigate = useNavigate();
  const friends = useInteractionStore((state) => state.friends);
  const isLoading = useInteractionStore((state) => state.isLoading);
  const onlineUsers = useInteractionStore((state) => state.onlineUsers);
  const fetchFriendsData = useInteractionStore((state) => state.fetchFriendsData);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchFriendsData?.();
  }, [fetchFriendsData, isLoggedIn]);

  if (!isLoggedIn) return null;

  const resolveAvatar = (friend) => {
    const raw =
      friend?.avatar ||
      friend?.profilePicture ||
      friend?.user?.avatar ||
      friend?.user?.profilePicture;
    const rawPath =
      typeof raw === 'string'
        ? raw
        : raw?.url || raw?.path || raw?.secure_url || raw?.publicUrl || '';

    if (rawPath) {
      const resolved = getMediaUrl(rawPath);
      if (import.meta.env.DEV) {
        console.log('[FriendsWidget] Avatar URL:', resolved, { raw: rawPath, friendId: friend?._id });
      }
      return resolved;
    }

    if (import.meta.env.DEV) {
      console.warn('[FriendsWidget] Missing avatar for friend:', friend?._id);
    }
    return avatarFallback;
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-4 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Mis Amigos</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">{friends?.length || 0}</span>
      </div>

      {isLoading ? (
        <div className="text-xs text-gray-500 dark:text-gray-400">Cargando amigos...</div>
      ) : friends && friends.length > 0 ? (
        <div className="space-y-3">
          {friends.slice(0, 6).map((friend) => {
            const friendId = friend?._id || friend?.user?._id;
            const isOnline = friend.isOnline ?? (friendId ? onlineUsers.includes(friendId.toString()) : false);
            return (
            <button
              key={friend._id}
              type="button"
              onClick={() => navigate(getProfileLink(friend))}
              className="flex items-center space-x-3 w-full text-left"
            >
              <div className="relative">
                <img
                  src={resolveAvatar(friend)}
                  alt={friend.username || friend.user?.username || 'Avatar'}
                  className={`w-9 h-9 rounded-full object-cover ${
                    friend.rank === 'senator' ||
                    friend.tier === 'senador' ||
                    friend.rank === 'patricio' ||
                    friend.tier === 'patricio'
                      ? 'gold-aura'
                      : ''
                  }`}
                  onError={(e) => {
                    e.currentTarget.src = avatarFallback;
                  }}
                />
                <span
                  className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-slate-800 ${
                    isOnline ? 'bg-green-500' : 'bg-gray-400'
                  }`}
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{friend.username || friend.user?.username || 'Usuario'}</p>
              </div>
            </button>
          );
          })}
        </div>
      ) : (
        <div className="text-xs text-gray-500 dark:text-gray-400">No tienes amigos aun.</div>
      )}
    </div>
  );
};

export default FriendsWidget;
